import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/v2/utils';
import { useUserStore } from '@/store/userStore';
import { useActiveOrg } from '@/store/workspaceStore';
import { WorkspaceMenuSection } from '@/v2/components/WorkspaceSwitcher';
import { CreateTeamModal } from '@/v2/components/CreateTeamModal';
import { useSettingsStore, LANGUAGES, type Language } from '@/store/settingsStore';
import { trackLanguageChanged } from '@/services/analyticsService';
import { isPaidPlan } from '@/utils/planHelpers';
import {
  ChevronDown,
  Crown,
  RefreshCw,
  Settings,
  Globe2,
  Gem,
  Users,
} from 'lucide-react';

export function Header() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  // Real session data (shared with V1 via the same stores/backend)
  const user = useUserStore((s) => s.user);
  const refreshUser = useUserStore((s) => s.refreshUser);
  // Real language preference (shared with V1 + AccountPage via settingsStore;
  // setLanguage already syncs i18next under the hood).
  const language = useSettingsStore((s) => s.language);
  const setLanguage = useSettingsStore((s) => s.setLanguage);
  // Team workspaces: the badge shows the org's POOLED credits in team context.
  const activeOrg = useActiveOrg(user);
  const credits = activeOrg ? activeOrg.remainingCredit : (user?.remainingCredit ?? 0);
  const displayName = user?.displayName || user?.name || t('header.defaultUser');
  const email = user?.email || '';
  const avatarUrl = user?.profilePictureUrl;
  // Expired subscribers (userPlan === 'expired', credits zeroed server-side) are
  // treated exactly like free everywhere else via isPaidPlan — mirror that here so
  // the header stops flashing a premium crown + "EXPIRED PLAN" + a stale "0" pill.
  const isPaid = isPaidPlan(user?.userPlan);
  const planLabel = isPaid
    ? t('header.planLabel', { plan: user!.userPlan!.toUpperCase() })
    : t('header.freePlan');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  // Owned HERE (not by the workspace section): the section unmounts when the
  // dropdown closes, so the modal must live above it. It portals to body.
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setShowLanguageDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await refreshUser(); } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  return (
    <header className="h-16 bg-surface/80 backdrop-blur-md border-b border-glass-border flex items-center justify-between pl-16 lg:pl-6 pr-4 sm:pr-6 sticky top-0 z-30">
      <div className="flex-1" />

      <div className="flex items-center gap-2 sm:gap-4">
        {/* Credits badge — team pool in a team workspace, personal otherwise */}
        <button
          onClick={() => navigate(activeOrg ? '/team?tab=billing' : '/subscription')}
          className="flex items-center gap-2 px-4 py-2 rounded-3xl bg-surface border border-primary/30 hover:border-primary/50 transition-all group"
        >
          {activeOrg ? (
            <>
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm text-text-secondary hidden sm:inline">{t('header.teamCredits')}</span>
              <div className="px-3 py-1 rounded-xl bg-gradient-to-r from-primary to-accent shadow-[0_0_12px_rgba(139,92,246,0.4)] group-hover:shadow-[0_0_16px_rgba(139,92,246,0.5)] transition-shadow">
                <span className="text-sm font-bold text-white tracking-wide">{Math.floor(credits)}</span>
              </div>
            </>
          ) : (
            <>
              <span className="text-sm text-text-secondary">{t('header.credits')}</span>
              <div className="px-3 py-1 rounded-xl bg-gradient-to-r from-primary to-accent shadow-[0_0_12px_rgba(139,92,246,0.4)] group-hover:shadow-[0_0_16px_rgba(139,92,246,0.5)] transition-shadow">
                <span className="text-sm font-bold text-white tracking-wide">{isPaid ? credits : t('header.free')}</span>
              </div>
            </>
          )}
        </button>

        {/* Refresh */}
        <button
          onClick={handleRefresh}
          className="p-2 rounded-xl hover:bg-surface-light transition-colors"
          title={t('header.refresh')}
        >
          <RefreshCw className={cn('w-5 h-5 text-text-secondary', isRefreshing && 'animate-spin')} />
        </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-surface-light transition-colors"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-8 h-8 rounded-full object-cover border border-glass-border"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 border border-glass-border flex items-center justify-center">
                <Gem className="w-4 h-4 text-primary-light" />
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-text-primary">{displayName}</p>
              {/* In a team workspace the subtitle shows WHERE you are (the team),
                  not the personal plan — that's what governs this context. */}
              {activeOrg ? (
                <p className="text-xs text-primary flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span className="max-w-[130px] truncate">{activeOrg.name}</span>
                </p>
              ) : (
                <p className="text-xs text-text-tertiary flex items-center gap-1">
                  {isPaid && <Crown className="w-3 h-3 text-amber-500" />}
                  {planLabel}
                </p>
              )}
            </div>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-text-tertiary transition-transform',
                isDropdownOpen && 'rotate-180'
              )}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 py-2 bg-surface border border-glass-border rounded-xl shadow-xl animate-fade-in">
              {/* User info */}
              <div className="px-4 py-3 border-b border-glass-border">
                <p className="text-sm font-medium text-text-primary truncate">
                  {displayName}
                </p>
                <p className="text-xs text-text-tertiary truncate">{email}</p>
              </div>

              {/* Workspace — Personal vs Team workspaces (Team plan lives here) */}
              <WorkspaceMenuSection
                onSwitched={() => setIsDropdownOpen(false)}
                onCreateTeam={() => { setIsDropdownOpen(false); setCreateTeamOpen(true); }}
              />

              {/* Quick settings */}
              <div className="py-2 border-b border-glass-border">
                <p className="px-4 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                  {t('header.quickSettings')}
                </p>

                {/* Real language switcher — backed by settingsStore (same source
                    as AccountPage). setLanguage already syncs i18next. */}
                <div className="relative">
                  <button
                    onClick={() => setShowLanguageDropdown((v) => !v)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
                  >
                    <Globe2 className="w-4 h-4 text-text-secondary" />
                    <span className="text-sm text-text-primary flex-1">{t('header.language')}</span>
                    <span className="text-base">{currentLanguage.flag}</span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 text-text-tertiary transition-transform',
                        showLanguageDropdown && 'rotate-180'
                      )}
                    />
                  </button>
                  {showLanguageDropdown && (
                    <div className="mt-1 mx-2 py-1 bg-surface-light border border-glass-border rounded-xl shadow-inner max-h-60 overflow-y-auto">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code as Language);
                            trackLanguageChanged(lang.code);
                            setShowLanguageDropdown(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface transition-colors',
                            language === lang.code && 'bg-primary/10 text-primary'
                          )}
                        >
                          <span className="text-base">{lang.flag}</span>
                          <span className="text-sm">{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Actions */}
              <div className="py-2">
                <button
                  onClick={() => {
                    navigate('/subscription');
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
                >
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-text-primary">{t('header.upgradePlan')}</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/account');
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
                >
                  <Settings className="w-4 h-4 text-text-secondary" />
                  <span className="text-sm text-text-primary">{t('header.settings')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create-team modal (portals itself to document.body — full-screen). */}
      {createTeamOpen && <CreateTeamModal onClose={() => setCreateTeamOpen(false)} />}
    </header>
  );
}
